import ClockIcon from "@mui/icons-material/AccessTime";
import { styled } from "@mui/material/styles";
import type { Duration } from "dayjs/plugin/duration";
import groupBy from "lodash/groupBy";

import { ProductStartTimeListNode } from "../../apiHooks/useProduct";
import { ReadMoreContainer } from "../../components/ExpandableContent";
import { LabelValue } from "../../components/LabelValue";
import { LabeledSection } from "../../components/LabeledSection";
import { useLanguage } from "../../hooks/useLanguage";
import { useProductTranslation } from "../../hooks/useProductTranslation";
import { durationFromString, formatDurationToTextAlternative } from "../../utils/formatters/formatDuration";

export type ProductSectionStartTimesProps = {
    startTimes: ProductStartTimeListNode[];
};

export function ProductSectionStartTimes({ startTimes }: ProductSectionStartTimesProps) {
    const groupedByDuration = groupBy(startTimes, "duration");
    const language = useLanguage();
    const [t] = useProductTranslation();
    const title = t("label.startTimes");
    return (
        <LabeledSection title={title} Icon={ClockIcon}>
            <ReadMoreContainer>
                <EntriesWrapper>
                    {Object.entries(groupedByDuration).map(([duration, startTimes], idx) => {
                        const finalDuration = durationFromString(language, duration);
                        if (!finalDuration) {
                            return null;
                        }
                        return (
                            <Entry key={idx} duration={finalDuration} startTimes={startTimes.map((x) => x.startTime)} />
                        );
                    })}
                </EntriesWrapper>
            </ReadMoreContainer>
        </LabeledSection>
    );
}

const EntriesWrapper = styled("div")(({ theme }) => ({
    display: "grid",
    gap: theme.spacing(3, 4),
    gridTemplateColumns: "max-content auto",
}));

function Entry({ duration, startTimes }: { duration: Duration; startTimes: Array<string | null> }) {
    const [t] = useProductTranslation();
    const durationLabel = t("label.duration");
    const startTimeLabel = t("label.startTime");
    const startTimesLabel = t("label.startTimes");
    return (
        <>
            <LabelValue label={durationLabel}>{formatDurationToTextAlternative(duration)}</LabelValue>
            <LabelValue label={startTimes.length > 1 ? startTimesLabel : startTimeLabel}>
                {startTimes.join(", ")}
            </LabelValue>
        </>
    );
}
